@import "./components/reset.css";
@import "./components/container.css";
@import "./components/media.css";
@import "./components/controls-backdrop.css";
@import "./components/buffering.css";
@import "./components/dialog.css";
@import "./components/controls.css";
@import "./components/time.css";
@import "./components/button.css";
@import "./components/button-group.css";
@import "./components/badge.css";
@import "./components/icons.css";
@import "./components/poster.css";
@import "./components/thumbnail.css";
@import "./components/slider.css";
@import "./components/popup.css";
@import "./components/menus.css";
@import "./components/captions.css";
@import "./components/input-indicator.css";
@import "./components/volume-indicator.css";
@import "./components/status-indicator.css";
@import "./components/seek-indicator.css";
@import "../../shared/css/video/icon-state.css";

/* Container */

.media-minimal-skin--video {
  --media-default-accent-color: oklch(1 0 0);
  --media-border-color: light-dark(oklch(0 0 0 / 0.15), oklch(1 0 0 / 0.15));
  --media-focus-ring-color: light-dark(oklch(0 0 0), oklch(1 0 0));
  --media-video-border-radius: var(--media-container-border-radius);

  --media-controls-background-color: transparent;
  --media-controls-transition-duration: 100ms;
  --media-controls-transition-timing-function: ease-out;

  --media-dialog-transition-duration: 150ms;
  --media-dialog-transition-delay: 100ms;
  --media-dialog-transition-timing-function: ease-out;

  --media-popup-transition-duration: 100ms;
  --media-popup-transition-timing-function: ease-out;

  --media-popover-backdrop-filter: blur(16px) saturate(1.5);
  --media-popover-background-color: oklch(0 0 0 / 0.5);
  --media-popover-border-color: oklch(1 0 0 / 0.1);

  --media-tooltip-backdrop-filter: var(--media-popover-backdrop-filter);
  --media-tooltip-background-color: var(--media-popover-background-color);
  --media-tooltip-border-color: var(--media-popover-border-color);

  --media-tooltip-text-color: currentColor;

  overflow: clip;
  background: oklch(0 0 0);

  @media (prefers-reduced-motion: reduce) {
    --media-dialog-transition-duration: 50ms;
    --media-dialog-transition-delay: 0ms;
    --media-popup-transition-duration: 0ms;

    .media-dialog__popup {
      scale: 1;
      transition-property: opacity;
    }
  }

  @media (prefers-reduced-transparency: reduce) or (prefers-contrast: more) {
    --media-controls-background-color: oklch(0 0 0);
    --media-tooltip-background-color: oklch(0 0 0);
  }

  &:has(.media-controls:not([data-visible])) {
    /* Slight delay to hide controls on non-touch devices after interaction */
    @media (pointer: fine) {
      --media-controls-transition-duration: 300ms;
    }
    @media (pointer: coarse) {
      --media-controls-transition-duration: 150ms;
    }
    @media (prefers-reduced-motion: reduce) {
      --media-controls-transition-duration: 50ms;
    }
  }

  /* Inner border ring */
  &::after {
    position: absolute;
    inset: 0;
    z-index: 10;
    pointer-events: none;
    content: "";
    border-radius: inherit;
    box-shadow: inset 0 0 0 1px var(--media-border-color);
  }

  &:fullscreen {
    --media-container-border-radius: 0;

    &::after {
      display: none;
    }

    @media (width >= 1280px) {
      --media-scale: 1.25;
    }
    @media (width >= 1536px) {
      --media-scale: 1.5;
    }
    @media (width >= 1920px) {
      --media-scale: 1.75;
    }
  }

  * {
    --media-focus-ring-color: oklch(1 0 0);
  }
}

/* Dialog */

.media-minimal-skin--video .media-dialog__popup {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: calc(var(--media-spacing) * 3);
  width: 100%;
  max-width: calc(var(--media-spacing) * 64);
  padding: calc(var(--media-spacing) * 4);
  color: oklch(1 0 0);
  text-shadow: 0 1px 0 oklch(0 0 0 / 0.5);
  translate: -50% -50%;
  transition-delay: var(--media-dialog-transition-delay);
  transition-timing-function: var(--media-dialog-transition-timing-function);
  transition-duration: var(--media-dialog-transition-duration);
  transition-property: opacity, scale;

  &[data-starting-style],
  &[data-ending-style],
  media-error-dialog[data-starting-style] &,
  media-error-dialog[data-ending-style] & {
    opacity: 0;
    scale: 0.95;
  }
  &[data-ending-style],
  media-error-dialog[data-ending-style] & {
    transition-delay: 0ms;
  }
}

.media-minimal-skin--video .media-dialog__content {
  display: flex;
  flex-direction: column;
  gap: calc(var(--media-spacing) * 2);
  padding: calc(var(--media-spacing) * 1.5) 0;
}

.media-minimal-skin--video .media-dialog__title {
  font-size: var(--media-font-size-medium);
}

/* Controls (hide/show behavior) */

.media-minimal-skin--video .media-controls {
  --media-base-side-offset: 5;
  --media-base-boundary-offset: 1;
  --media-inset-factor: 1;
  --media-inset: calc(var(--media-spacing) * var(--media-inset-factor));
  --media-volume-mask: linear-gradient(to right, transparent 10%, #000 25%, #000 100%);

  position: absolute;
  inset-inline: var(--media-inset);
  bottom: var(--media-inset);
  z-index: 10;
  flex-wrap: wrap;
  column-gap: calc(var(--media-spacing) * 2);
  color: oklch(1 0 0);
  border-radius: calc(var(--media-spacing) * 3);
  transition-timing-function: var(--media-controls-transition-timing-function);
  /* Speed up the entry transition */
  transition-duration: calc(var(--media-controls-transition-duration) / 2);

  @media (pointer: fine) {
    transition-property: translate, filter, opacity;
  }

  @media (pointer: coarse) {
    transition-property: translate, opacity;
  }

  &:not([data-visible]) {
    pointer-events: none;
    opacity: 0;
    transition-duration: var(--media-controls-transition-duration);

    @media (pointer: fine) and (prefers-reduced-motion: no-preference) {
      filter: blur(8px);
    }

    @media (prefers-reduced-motion: no-preference) {
      translate: 0 100%;
    }
  }

  .media-time-controls {
    --media-slider-height: calc(var(--media-spacing) * 5);

    flex: 0 0 100%;
    order: -1;
    padding-inline: calc(var(--media-spacing) * 1.5);
  }

  .media-button-group:first-child {
    flex: 1;
    text-align: left;
  }

  .media-button-group:last-child {
    flex: 1;
    justify-content: end;
  }

  :is(.media-time-controls, .media-button-group:last-child) {
    mask-image: var(--media-volume-mask-image, none);
    mask-repeat: no-repeat;
    mask-position: var(--media-volume-mask-position, 100% 0);
    mask-size: var(--media-volume-mask-size, 200% 100%);
    transition: mask-position 50ms ease-out;
  }

  &:has(.media-button--mute[aria-expanded="true"]) {
    @container media-root (width <= 42rem) {
      .media-button-group:last-child {
        --media-volume-mask-image: var(--media-volume-mask);
        --media-volume-mask-position: 0 0;
        --media-volume-mask-size: 400% 100%;
      }
    }

    @container media-root (width > 42rem) {
      .media-time-controls {
        --media-volume-mask-image: var(--media-volume-mask);
        --media-volume-mask-position: 0 0;
      }
    }
  }

  @container media-root (width > 42rem) {
    --media-inset-factor: 2;
    --media-base-side-offset: 2;

    flex-wrap: nowrap;

    .media-time-controls {
      --media-slider-height: calc(var(--media-spacing) * 8);

      flex: 1;
      order: unset;
    }

    .media-button-group:first-child,
    .media-button-group:last-child {
      flex: 0 0 auto;
    }
  }
}

/* Hide cursor when controls are hidden */
.media-minimal-skin--video:has(.media-controls:not([data-visible])) {
  cursor: none;
}

/* Popups & Animations */

.media-minimal-skin--video .media-popover--volume {
  --media-popover-side-offset: 0rem;
  padding: 0 calc(var(--media-spacing) * 3);
  background: transparent;
}

/* Slider preview */

.media-minimal-skin--video .media-slider__value {
  padding-inline: calc(var(--media-spacing) * 3);
  text-shadow: 0 1px 0 var(--media-shadow-current-color);
}

.media-minimal-skin--video .media-slider .media-slider__preview {
  --media-preview-end-inset: calc(100cqi - 100%);
  --media-preview-left: clamp(
    calc(var(--media-max-size) / 2),
    var(--media-slider-pointer),
    calc(100% - var(--media-max-size) / 2 + var(--media-preview-end-inset))
  );

  @container media-root (width > 42rem) {
    --media-preview-left: var(--media-slider-pointer);
  }
}
